import { AUTH_SESSION_COOKIE, AUTH_SESSION_VALUE } from "./auth-session-constants";

export { AUTH_SESSION_COOKIE, AUTH_SESSION_VALUE };

export const AUTH_TOKEN_KEY = "authToken";
export const AUTH_USER_KEY = "authUser";

export type StoredAuthUser = {
  userId: string;
  name: string;
  email: string;
  role?: string;
  tenantId?: string;
  tenantName?: string;
};

export type AuthSessionSnapshot = {
  token: string | null;
  user: StoredAuthUser | undefined;
};

const listeners = new Set<() => void>();
let snapshot: AuthSessionSnapshot | undefined;

function readToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(AUTH_TOKEN_KEY)?.trim() || null;
}

function readUser(): StoredAuthUser | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(AUTH_USER_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as StoredAuthUser;
    if (!parsed?.userId || !parsed?.name) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

function readSnapshot(): AuthSessionSnapshot {
  return { token: readToken(), user: readUser() };
}

function notify(): void {
  const next = readSnapshot();
  if (
    snapshot &&
    snapshot.token === next.token &&
    snapshot.user?.userId === next.user?.userId &&
    snapshot.user?.email === next.user?.email
  ) {
    return;
  }
  snapshot = next;
  listeners.forEach((listener) => listener());
}

/** Re-read localStorage and push to subscribers (e.g. after mount). */
export function refreshAuthSessionFromStorage(): void {
  notify();
}

export function subscribeAuthSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getAuthSessionSnapshot(): AuthSessionSnapshot {
  const next = readSnapshot();
  if (
    snapshot &&
    snapshot.token === next.token &&
    snapshot.user?.userId === next.user?.userId &&
    snapshot.user?.email === next.user?.email
  ) {
    return snapshot;
  }
  snapshot = next;
  return snapshot;
}

export function getServerAuthSnapshot(): AuthSessionSnapshot {
  return { token: null, user: undefined };
}

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return getAuthSessionSnapshot().token;
}

export function readStoredAuthUser(): StoredAuthUser | undefined {
  if (typeof window === "undefined") return undefined;
  return getAuthSessionSnapshot().user;
}

/** Companion cookie so Edge can authorize /api/* (JWT stays in localStorage). */
export function setAuthSessionCookiePresent(): void {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${AUTH_SESSION_COOKIE}=${AUTH_SESSION_VALUE}; Path=/; Max-Age=${60 * 60 * 24 * 7}; SameSite=Lax${secure}`;
}

export function clearAuthSessionCookie(): void {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${AUTH_SESSION_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
}

export function persistAuthSession(token: string, user: StoredAuthUser): void {
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
  window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  setAuthSessionCookiePresent();
  notify();
}

export function clearAuthSession(): void {
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  window.localStorage.removeItem(AUTH_USER_KEY);
  clearAuthSessionCookie();
  notify();
}

/** Inline head script: sync cookie from localStorage before React/API calls. */
export function authCookieBootstrapScript(): string {
  return `(function(){try{if(localStorage.getItem(${JSON.stringify(AUTH_TOKEN_KEY)})){var s=location.protocol==="https:"?"; Secure":"";document.cookie=${JSON.stringify(`${AUTH_SESSION_COOKIE}=${AUTH_SESSION_VALUE}; Path=/; Max-Age=604800; SameSite=Lax`)}+s;}}catch(e){}})();`;
}
